import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid2 as Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    debounce,
} from "@mui/material"
import { grey } from "@mui/material/colors"
import { ChangeEvent, useCallback, useState } from "react"
import { Subtitle2 } from "../../components/Typography/Typography"
import { DataReceiptTableFilters } from "../../models/DataReceiptQueries"
import { OfficeId, officeIdOptions } from "../../store/types/MissionsTypes"
import { TimeUtilsDateFns } from "../../utils/TimeUtils"
import { useDataReceiptStore } from "../../zstore/DataReceiptStore"
import { initTableFilters } from "../../zstore/DataReceiptTableSlice"

// to make sure the filters stay on the same level and don't overflow when too small,
// we are hardcoding the width (max and min width would cause wrapping or overflow)
const smallInputWidth = 160
const largeInputWidth = 220
const dropDownInputWidth = 120

interface MissionFilterField {
    label: string
    field: keyof DataReceiptTableFilters
    value: string
    width?: number
}

export const SelectMissionFilters = () => {
    const filters = useDataReceiptStore((state) => state.filters)
    const [userFilters, setUserFilters] = useState<DataReceiptTableFilters>(filters)

    const setFilter = useDataReceiptStore((state) => state.setFilter)
    const clearFilters = useDataReceiptStore((state) => state.clearFilters)
    const setTableIsLoading = useDataReceiptStore((state) => state.setTableIsLoading)

    const fields: MissionFilterField[] = [
        { label: "Mission Name", field: "mission", value: userFilters.mission, width: smallInputWidth },
        {
            label: "Project Name or Number",
            field: "project",
            value: userFilters.project,
            width: largeInputWidth,
        },
        { label: "Source", field: "source", value: userFilters.source, width: smallInputWidth },
        {
            label: "Tracking Number",
            field: "tracking_number",
            value: userFilters.tracking_number,
            width: smallInputWidth,
        },
    ]

    const debounceFilters = useCallback(
        // Wait 1500ms seconds before updating the filters in zustand
        // -> updating the filters that call the db
        debounce(<T extends keyof DataReceiptTableFilters>(field: T, value: DataReceiptTableFilters[T]) => {
            setFilter(field, value)
            setTableIsLoading(false)
        }, 1500),
        [],
    )

    const handleChange = <T extends keyof DataReceiptTableFilters>(field: T, value: DataReceiptTableFilters[T]) => {
        // Sets temp state for what the user sees - keeps even when clicking away on stepper
        setUserFilters({ ...userFilters, [field]: value })
        setTableIsLoading(true)
        debounceFilters(field, value)
    }

    const handleShowMissionsOlderThanSixtyDays = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        // If it is checked have no limit, otherwise force a limit of the last 60 days
        const val = checked ? "" : TimeUtilsDateFns.getFormattedNumberOfDaysAgo(60)
        setFilter("mission_flown_date", val)
        setUserFilters({ ...userFilters, mission_flown_date: val })
    }

    const handleChangeOfficeSelection = (offices: OfficeId[]) => {
        setFilter("office", offices)
        setUserFilters({ ...userFilters, office: offices })
    }

    const handleClearFilters = () => {
        setUserFilters(initTableFilters())
        clearFilters()
    }

    return (
        <Grid
            container
            spacing={2}
            sx={{
                p: 2,
                backgroundColor: grey[50],
                // Took the MUI border to match the table
                borderBottom: "1px solid rgba(224,224,224,1)",
            }}
        >
            <Grid container size={12} sx={{ justifyContent: "space-between" }}>
                <Grid sx={{ display: "flex", alignItems: "flex-end" }}>
                    <Subtitle2 disableGutter={true} sx={{ margin: 0 }}>
                        Filters
                    </Subtitle2>
                </Grid>
                <Grid sx={{ display: "flex", justifyContent: "center" }}>
                    <Button color="primary" onClick={handleClearFilters}>
                        Clear Filters
                    </Button>
                </Grid>
            </Grid>
            <Grid display={"flex"} gap={2}>
                {fields.map(({ label, field, value }) => (
                    <TextField
                        key={label}
                        label={label}
                        value={value}
                        onChange={(e) => handleChange(field, e.target.value)}
                        sx={{ width: 228 }}
                    />
                ))}
                <FormControl sx={{ width: dropDownInputWidth }}>
                    <InputLabel>Office</InputLabel>
                    <Select
                        variant={"outlined"}
                        value={userFilters.office}
                        onChange={(e) => handleChangeOfficeSelection(e.target.value as OfficeId[])}
                        label={"Office"}
                        multiple={true}
                    >
                        {officeIdOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={userFilters.mission_flown_date === ""}
                            onChange={handleShowMissionsOlderThanSixtyDays}
                        />
                    }
                    label={"Display Shipments Older than 60 Days"}
                />
            </Grid>
        </Grid>
    )
}
